import { useState, useEffect, useMemo, useRef } from 'react';
import {
  VenetianMask, Palette, Type, Smile, AtSign, Share2, UserCheck, Users,
  Sparkles, Upload, ShieldCheck, MessageSquare, Clock, Search, Send,
} from 'lucide-react';
import { The, AnhDaiDien, DangTai, ChuaCoDuLieu } from '../components/ui';
import { api, LoiMayChu } from '../services/api';
import type { Nhom, TrangThaiAI } from '../services/kieu';
import { choPhepAnDanh } from '../services/taiKhoan';

/**
 * Màn soạn và gửi bài đăng.
 *
 * Bố cục, nhãn và lớp Tailwind lấy từ bản đang chạy (đọc DOM qua CDP).
 * Quy tắc nghiệp vụ lấy từ `lab-server/schemas/dispatch.schema.js` — nguồn
 * sự thật duy nhất, vì máy chủ mới là nơi quyết định chấp nhận hay từ chối.
 */

// ===== Hằng số lấy từ dispatch.schema.js =====
const TOI_DA_KY_TU = 63206;
const TOI_DA_NHOM = 100;
const GIAN_CACH_TOI_THIEU = 10;
const GIAN_CACH_TOI_DA = 3600;
/** Nêu bật quét spam tag rất gắt -> sàn giãn cách ×3 (schema dòng 51-54). */
const GIAN_CACH_TOI_THIEU_NEU_BAT = 30;
const TOI_DA_DANG_CHEO = 9;
const TOI_DA_BINH_LUAN = 2000;
/** Phông nền: Facebook bóc nền nếu bài dài hoặc nhiều dòng (schema dòng 38-45). */
const PHONG_NEN_TOI_DA_KY_TU = 130;
const PHONG_NEN_TOI_DA_DONG = 4;

const NUT_CONG_CU = 'inline-flex h-8 w-8 items-center justify-center rounded-lg transition';
const NUT_THUONG = 'text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700';
const NUT_BAT = 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400';
const NUT_KHOA = 'cursor-not-allowed text-slate-300 dark:text-slate-600';
const O_SO = 'w-16 rounded-md border border-slate-300 px-2 py-1.5 text-center text-sm font-semibold text-slate-800 outline-none focus:border-brand-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100';
const KHOI_TUY_CHON = 'mt-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700';

function CongTac({ bat }: { bat: boolean }) {
  return (
    <span className={'relative h-5 w-9 rounded-full transition-colors ' + (bat ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600')}>
      <span className={'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ' + (bat ? 'left-[1.125rem]' : 'left-0.5')} />
    </span>
  );
}

export function ManTaoBai({ onXong, fbUid, dangDungPage = false }: { onXong?: () => void; fbUid?: string; dangDungPage?: boolean }) {
  const [noiDung, setNoiDung] = useState('');
  const [nhom, setNhom] = useState<Nhom[]>([]);
  const [daChon, setDaChon] = useState<Set<string>>(new Set());
  const [timNhom, setTimNhom] = useState('');
  const [dangTaiNhom, setDangTaiNhom] = useState(true);
  const [lyDoTrongNhom, setLyDoTrongNhom] = useState<string | null>(null);

  const [anDanh, setAnDanh] = useState(false);
  const [neuBat, setNeuBat] = useState(false);
  const [dangCheo, setDangCheo] = useState(false);
  const [dangLenTrang, setDangLenTrang] = useState(false);

  const [tuGiay, setTuGiay] = useState(60);
  const [denGiay, setDenGiay] = useState(120);
  const [neSpam, setNeSpam] = useState(true);

  const [batBinhLuan, setBatBinhLuan] = useState(false);
  const [binhLuan, setBinhLuan] = useState('');

  const [henGio, setHenGio] = useState('');
  const [lapHangNgay, setLapHangNgay] = useState(false);

  const [ai, setAi] = useState<TrangThaiAI | null>(null);
  const [dangGui, setDangGui] = useState(false);
  const [thongBao, setThongBao] = useState<{ loai: 'loi' | 'xong'; chu: string } | null>(null);
  const oNhap = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    api.nhom(fbUid)
      .then(r => { setNhom(r.groups || []); setLyDoTrongNhom(r.reason ? (r.message || r.reason) : null); })
      .catch(e => setLyDoTrongNhom(e instanceof LoiMayChu ? e.message : 'Không tải được danh sách nhóm.'))
      .finally(() => setDangTaiNhom(false));
    api.trangThaiAI().then(setAi).catch(() => {});
  }, [fbUid]);

  useEffect(() => { if (dangDungPage) setAnDanh(false); }, [dangDungPage]);

  // Nêu bật ép sàn giãn cách lên 30s. Máy chủ cũng chặn, nhưng sửa sẵn ở đây
  // để khách không bị từ chối sau khi đã soạn xong bài.
  useEffect(() => {
    if (neuBat && tuGiay < GIAN_CACH_TOI_THIEU_NEU_BAT) {
      setTuGiay(GIAN_CACH_TOI_THIEU_NEU_BAT);
      setDenGiay(d => Math.max(d, GIAN_CACH_TOI_THIEU_NEU_BAT));
    }
  }, [neuBat, tuGiay]);

  const nhomLoc = useMemo(() => {
    const k = timNhom.trim().toLowerCase();
    return k ? nhom.filter(g => g.name.toLowerCase().includes(k)) : nhom;
  }, [nhom, timNhom]);

  const soMucTieu = daChon.size + (dangLenTrang ? 1 : 0);

  /**
   * Kiểm tra TRƯỚC khi gửi, dùng đúng quy tắc của schema máy chủ.
   * Thà báo lỗi ngay tại chỗ còn hơn để khách bấm gửi rồi nhận 400.
   */
  const loiHopLe = useMemo((): string | null => {
    if (!noiDung.trim()) return 'Hãy nhập nội dung bài viết.';
    if (noiDung.length > TOI_DA_KY_TU) return 'Nội dung bài viết quá dài.';
    if (soMucTieu === 0) return 'Vui lòng chọn ít nhất một nhóm hoặc chọn đăng lên Trang cá nhân.';
    if (daChon.size > TOI_DA_NHOM) return `Mỗi chiến dịch chỉ được chọn tối đa ${TOI_DA_NHOM} nhóm.`;
    if (!Number.isFinite(tuGiay) || !Number.isFinite(denGiay)) return 'Khoảng nghỉ không hợp lệ.';
    if (tuGiay < GIAN_CACH_TOI_THIEU) return `Khoảng nghỉ tối thiểu là ${GIAN_CACH_TOI_THIEU} giây.`;
    if (denGiay < tuGiay) return 'Khoảng nghỉ kết thúc phải lớn hơn hoặc bằng khoảng bắt đầu.';
    if (denGiay > GIAN_CACH_TOI_DA) return 'Khoảng nghỉ tối đa là 3.600 giây.';
    if (neuBat && tuGiay < GIAN_CACH_TOI_THIEU_NEU_BAT) {
      return 'Khi bật Nêu bật, khoảng nghỉ tối thiểu là 30 giây để tránh bị Facebook khoá tài khoản.';
    }
    if (dangCheo && anDanh) {
      return 'Không thể dùng Đăng chéo cùng Đăng ẩn danh: mỗi nhóm có một hồ sơ ảo riêng nên Facebook sẽ từ chối cả gói.';
    }
    if (dangCheo && daChon.size > TOI_DA_DANG_CHEO + 1) {
      return `Đăng chéo tối đa ${TOI_DA_DANG_CHEO} nhóm (Facebook giới hạn 10 nhóm mỗi bài, tính cả nhóm đăng chính).`;
    }
    if (batBinhLuan && binhLuan.length > TOI_DA_BINH_LUAN) return 'Bình luận tối đa 2.000 ký tự.';
    if (henGio) {
      const at = new Date(henGio).getTime();
      if (!Number.isFinite(at)) return 'Thời điểm hẹn đăng không hợp lệ.';
      if (at < Date.now() - 60000) return 'Thời điểm hẹn đăng phải ở tương lai.';
    }
    return null;
  }, [noiDung, soMucTieu, daChon.size, tuGiay, denGiay, neuBat, dangCheo, anDanh, batBinhLuan, binhLuan, henGio]);

  async function gui() {
    if (loiHopLe) { setThongBao({ loai: 'loi', chu: loiHopLe }); return; }
    setDangGui(true);
    setThongBao(null);
    try {
      const than: Record<string, unknown> = {
        message: noiDung,
        targets: [...daChon],
        delayFromSec: tuGiay,
        delayToSec: denGiay,
        safeMode: neSpam,
        fbUid,
      };
      if (dangLenTrang) than.postToFeed = true;
      if (choPhepAnDanh(dangDungPage, anDanh)) than.isAnonymous = true;
      if (neuBat) than.useHighlight = true;
      if (dangCheo) { than.crossPost = true; than.crossPostGroupIds = [...daChon].slice(1); }
      if (batBinhLuan && binhLuan.trim()) than.firstComment = binhLuan.trim();
      if (henGio) than.scheduledAt = new Date(henGio).getTime();
      if (lapHangNgay) than.repeatDaily = true;

      const kq = await api.taoChienDich(than);
      setThongBao({ loai: 'xong', chu: kq.message || 'Đã tạo tác vụ đăng bài.' });
      setNoiDung('');
      setDaChon(new Set());
      setBinhLuan('');
      onXong?.();
    } catch (e) {
      setThongBao({ loai: 'loi', chu: e instanceof LoiMayChu ? e.message : 'Không gửi được yêu cầu.' });
    } finally {
      setDangGui(false);
    }
  }

  function nutCongCu(
    Icon: typeof VenetianMask, nhan: string, bat: boolean,
    doi: () => void, khoa = false,
  ) {
    return (
      <button
        type="button"
        title={nhan}
        aria-label={nhan}
        disabled={khoa}
        onClick={doi}
        className={NUT_CONG_CU + ' ' + (khoa ? NUT_KHOA : bat ? NUT_BAT : NUT_THUONG)}
      >
        <Icon className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in">
      <div className="grid min-h-[calc(100vh-8.5rem)] gap-5 xl:grid-cols-[minmax(0,1fr)_440px]">

        {/* ===== Cột trái: soạn bài ===== */}
        <The className="flex flex-col p-5">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Soạn bài đăng</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Ảnh/video được lưu trên thiết bị trong Extension và upload trực tiếp tới Facebook.
            </p>
          </div>

          <div className="mt-5 flex min-h-[240px] flex-1 flex-col rounded-xl border border-slate-200 bg-slate-50/50 dark:border-slate-700 dark:bg-slate-900/40">
            <textarea
              ref={oNhap}
              value={noiDung}
              onChange={(e) => setNoiDung(e.target.value)}
              placeholder="Bạn muốn chia sẻ nội dung gì?"
              className="flex-1 resize-none bg-transparent px-4 py-3 text-sm leading-6 text-slate-700 outline-none dark:text-slate-200"
            />
            <div className="flex items-center justify-between px-2 pb-2">
              <div className="relative flex items-center gap-0.5">
                {nutCongCu(VenetianMask, dangDungPage ? 'Page không đăng ẩn danh — chuyển sang tài khoản cá nhân' : 'Đăng ẩn danh — chỉ dùng cho Nhóm', anDanh, () => setAnDanh(v => !v), dangDungPage)}
                <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
                {nutCongCu(Palette, 'Phông nền', false, () => {}, noiDung.length > PHONG_NEN_TOI_DA_KY_TU || noiDung.split('\n').length > PHONG_NEN_TOI_DA_DONG)}
                {nutCongCu(Type, 'Trình soạn thảo văn bản', false, () => {})}
                {nutCongCu(Smile, 'Chèn emoji', false, () => {})}
                {nutCongCu(AtSign, 'Gắn thẻ bạn bè (đang phát triển)', false, () => {}, true)}
                {nutCongCu(Share2, 'Đăng chéo — một bài hiển thị ở nhiều nhóm cùng lúc (tối đa 9 nhóm)', dangCheo, () => setDangCheo(v => !v))}
                {nutCongCu(UserCheck, 'Nêu bật — tự tăng giãn cách 3 lần để tránh bị khoá tài khoản', neuBat, () => setNeuBat(v => !v))}
                {nutCongCu(Users, 'Đăng lên Trang cá nhân', dangLenTrang, () => setDangLenTrang(v => !v))}
              </div>

              <button
                type="button"
                disabled={!ai?.available}
                title={ai?.available ? 'AI Spin nội dung' : (ai?.reason || 'AI chưa sẵn sàng')}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-semibold text-violet-700 transition disabled:cursor-not-allowed disabled:opacity-50 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-400"
              >
                <Sparkles className="h-3.5 w-3.5" />
                AI Spin nội dung
              </button>
            </div>
          </div>

          <div className="mt-2 flex justify-between text-xs text-slate-400">
            <span>{anDanh && <span className="text-amber-600">Đăng ẩn danh — chỉ dùng cho Nhóm</span>}</span>
            <span>{noiDung.length.toLocaleString('vi-VN')} ký tự</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
            >
              <Upload className="h-4 w-4" />
              Thêm ảnh/video
            </button>
            <button type="button" className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white">
              Lưu bản nháp
            </button>
          </div>

          {/* Giãn cách + né spam */}
          <div className="mt-5 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Cách nhau</span>
                <input
                  type="number" value={tuGiay} min={neuBat ? GIAN_CACH_TOI_THIEU_NEU_BAT : GIAN_CACH_TOI_THIEU}
                  onChange={(e) => setTuGiay(Number(e.target.value))} className={O_SO}
                />
                <span className="text-xs text-slate-400">đến</span>
                <input
                  type="number" value={denGiay} max={GIAN_CACH_TOI_DA}
                  onChange={(e) => setDenGiay(Number(e.target.value))} className={O_SO}
                />
                <span className="text-xs text-slate-500 dark:text-slate-400">giây mỗi nhóm</span>
              </div>
              <button type="button" onClick={() => setNeSpam(v => !v)} className="flex items-center gap-2">
                <CongTac bat={neSpam} />
                <ShieldCheck className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Né spam</span>
              </button>
            </div>
            {neuBat && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                Nêu bật đang bật: giãn cách tối thiểu {GIAN_CACH_TOI_THIEU_NEU_BAT} giây để tránh Facebook khoá tài khoản.
              </p>
            )}
          </div>

          {/* Bình luận sau khi đăng */}
          <div className={KHOI_TUY_CHON}>
            <button type="button" onClick={() => setBatBinhLuan(v => !v)} className="flex items-center gap-2">
              <CongTac bat={batBinhLuan} />
              <MessageSquare className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Bình luận sau khi đăng</span>
            </button>
            {batBinhLuan && (
              <textarea
                value={binhLuan}
                onChange={(e) => setBinhLuan(e.target.value)}
                placeholder="Nội dung bình luận tự đăng sau khi bài lên..."
                rows={2}
                className="mt-2 w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            )}
          </div>

          {/* Thời gian đăng */}
          <div className={KHOI_TUY_CHON}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Thời gian đăng</span>
              </div>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox" checked={lapHangNgay} onChange={(e) => setLapHangNgay(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                Lặp lại hàng ngày
              </label>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                type="datetime-local" value={henGio} onChange={(e) => setHenGio(e.target.value)}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 outline-none focus:border-brand-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
              />
              <span className="text-xs text-slate-400">Bỏ trống để đăng ngay</span>
            </div>
          </div>
        </The>

        {/* ===== Cột phải: chọn nhóm ===== */}
        <The className="flex flex-col">
          <div className="border-b p-4 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-slate-900 dark:text-white">Chọn nhóm đăng</h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Đã chọn {daChon.size}/{nhom.length} nhóm
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDaChon(daChon.size === nhomLoc.length ? new Set() : new Set(nhomLoc.map(g => g.id)))}
                className="px-3 py-2 text-xs font-semibold text-brand-600 dark:text-brand-400"
              >
                {daChon.size === nhomLoc.length && nhomLoc.length > 0 ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
              </button>
            </div>
            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={timNhom} onChange={(e) => setTimNhom(e.target.value)} placeholder="Tìm nhóm..."
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>

          <div className="min-h-[360px] flex-1 space-y-2 overflow-y-auto p-3 scrollbar-thin">
            {dangTaiNhom ? (
              <DangTai dong={5} />
            ) : nhomLoc.length === 0 ? (
              <ChuaCoDuLieu
                chu={timNhom ? 'Không tìm thấy nhóm nào khớp.' : 'Chưa có nhóm nào.'}
                goiY={lyDoTrongNhom || (timNhom ? undefined : 'Vào Kênh & nhóm để đồng bộ danh sách.')}
              />
            ) : (
              nhomLoc.map((g) => {
                const chon = daChon.has(g.id);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => {
                      const m = new Set(daChon);
                      if (m.has(g.id)) m.delete(g.id); else m.add(g.id);
                      setDaChon(m);
                    }}
                    className={
                      'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ' +
                      (chon
                        ? 'border-brand-500 bg-brand-50/50 dark:border-brand-500 dark:bg-brand-500/10'
                        : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/40')
                    }
                  >
                    <span className={
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border ' +
                      (chon ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 dark:border-slate-600')
                    }>
                      {chon && <span className="text-[11px] leading-none">✓</span>}
                    </span>
                    <AnhDaiDien src={g.avatar} ten={g.name} kichThuoc="h-8 w-8" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{g.name}</span>
                      <span className="text-xs text-slate-400">Nhóm Facebook</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t p-4 dark:border-slate-700">
            {thongBao && (
              <p className={
                'mb-3 rounded-lg px-3 py-2 text-xs ' +
                (thongBao.loai === 'loi'
                  ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'
                  : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400')
              }>
                {thongBao.chu}
              </p>
            )}
            {loiHopLe && !thongBao && (
              <p className="mb-3 text-xs text-slate-400">{loiHopLe}</p>
            )}
            <button
              type="button"
              onClick={gui}
              disabled={dangGui || Boolean(loiHopLe)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {dangGui ? 'Đang gửi...' : `Đăng vào ${soMucTieu} mục tiêu`}
            </button>
          </div>
        </The>
      </div>
    </div>
  );
}
